<template>
  <li class="demographics-item">
    <div class="age-dropdown">
      <FormSelector
        placeholder="Age"
        :data="ageData"
        v-model="demoItem.ageId"
        :error="demoItem.error"
        :grayed="grayed"
        @update:modelValue="updateAge"
      />
    </div>

    <div class="gender-dropdown">
      <FormSelector
        placeholder="Gender"
        :data="genderData"
        v-model="demoItem.genderId"
        :error="demoItem.error"
        :grayed="grayed"
        @update:modelValue="updateGender"
      />
    </div>

    <div class="participant-dropdown">
      <FormCounter
        placeholder="# Participant"
        v-model="demoItem.numParticipants"
        :error="demoItem.error"
        :grayed="grayed"
        @update:modelValue="updateParticipants"
      />
    </div>

    <div class="button-container">
      <transition name="fade">
        <div
          :class="{ 'delete-button': true, grayed }"
          v-if="deletable"
          @click="$emit('remove')"
          @keypress.enter="$emit('remove')"
        >
          <inline-svg :src="$assets.svg['deleteIcon']" height="100%" />
        </div>
      </transition>
    </div>
  </li>
</template>

<script setup>
import { computed } from 'vue';

import $assets from '@/assets';
import FormCounter from '@/components/form/elements/FormCounter.vue';
import FormSelector from '@/components/form/elements/FormSelector.vue';

const emit = defineEmits(['update']);
const props = defineProps({
  data: {
    type: Object,
    default: () => {
      return {};
    },
  },
  deletable: Boolean,
  ageData: {
    type: Array,
    default: () => {
      return [];
    },
  },
  genderData: {
    type: Array,
    default: () => {
      return [];
    },
  },
  grayed: Boolean,
});

const demoItem = computed({
  get() {
    return props.data;
  },
  set(newValue) {
    emit('update', newValue);
  },
});

// using Object.assign to trigger demoItem reactivity
function updateAge(ageId) {
  demoItem.value = { ...demoItem.value, ageId: Number.parseInt(ageId, 10) };
}

function updateGender(genderId) {
  demoItem.value = { ...demoItem.value, genderId: Number.parseInt(genderId, 10) };
}

function updateParticipants(numParticipants) {
  const parsedNumParticipants = numParticipants ? Number.parseInt(numParticipants, 10) : null;
  demoItem.value = {
    ...demoItem.value,
    numParticipants: parsedNumParticipants < 0 ? 0 : parsedNumParticipants, // can't go below 0
  };
}
</script>

<style scoped>
.demographics-item {
  display: grid;
  flex-wrap: wrap;
  justify-content: space-between;
  grid-template-areas:
    'a . . .'
    'b b b b'
    'c . . d';
  row-gap: 0.4em;
  column-gap: 0.4em;

  margin-top: 2em;
}

.age-dropdown {
  grid-area: a;
}
.gender-dropdown {
  grid-area: b;
}
.participant-dropdown {
  grid-area: c;
  width: 10em;
}

.button-container {
  display: flex;
  align-items: center;
  grid-area: d;
  width: 2em;
}
.delete-button {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 1.4em;
  height: 1.4em;
  cursor: pointer;
}
.delete-button :deep(svg),
.delete-button :deep(svg) path {
  stroke: #000;
}
.delete-button.grayed :deep(svg),
.delete-button.grayed :deep(svg) path {
  stroke: #6d6e71;
}
.delete-button:hover :deep(svg),
.delete-button:hover :deep(svg) path {
  stroke: #e71e25;
}

/** Larger devices */
@media only screen and (min-width: 601px) and (min-height: 601px) {
  .demographics-item {
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-start;
    max-width: 40em;
  }

  .age-dropdown {
    width: 5em;
  }
  .gender-dropdown {
    flex-grow: 1;
  }
  .participant-dropdown {
    width: 9em;
  }

  .button-container {
    width: 2em;
  }
}
</style>
